import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Save, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Read the logged-in user from localStorage. Returns null if anything is off
// so the form falls back to a sane empty state.
const readCurrentUser = () => {
  try {
    const raw = localStorage.getItem('currentUser');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Profile = () => {
  const [user, setUser] = useState(() => readCurrentUser());
  const [draft, setDraft] = useState(() => {
    const u = readCurrentUser();
    return {
      name: u?.name || '',
      email: u?.email || '',
      credentials: u?.credentials || '',
    };
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', message }

  const setField = (key) => (e) => {
    setDraft((d) => ({ ...d, [key]: e.target.value }));
    setFeedback(null);
  };

  const handleSave = async () => {
    if (!user?.token) {
      setFeedback({ type: 'error', message: 'You are not logged in.' });
      return;
    }
    if (!draft.name.trim()) {
      setFeedback({ type: 'error', message: 'Please enter your name.' });
      return;
    }
    if (!draft.email.trim()) {
      setFeedback({ type: 'error', message: 'Please enter your email.' });
      return;
    }
    try {
      setSaving(true);
      setFeedback(null);
      const { data } = await axios.put(
        `${API_URL}/api/auth/profile`,
        {
          name: draft.name,
          email: draft.email,
          credentials: draft.credentials,
        },
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      // Merge fresh fields into the cached currentUser so the rest of the app
      // (TopBar, Settings page, future requests) sees the new values.
      const merged = { ...user, ...data };
      localStorage.setItem('currentUser', JSON.stringify(merged));
      setUser(merged);
      setDraft({
        name: merged.name || '',
        email: merged.email || '',
        credentials: merged.credentials || '',
      });
      setFeedback({ type: 'success', message: 'Profile updated.' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setFeedback({
        type: 'error',
        message: detail || 'Could not update your profile. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="space-y-6"
      data-testid="profile-page"
    >
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Profile</h1>
        <p className="text-muted-foreground mt-1">
          Update your personal information. Role is managed by an admin.
        </p>
      </div>

      <Card className="p-6 shadow-sm max-w-xl" data-testid="profile-card">
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="profile-name">Full Name</Label>
            <Input
              id="profile-name"
              value={draft.name}
              onChange={setField('name')}
              data-testid="profile-input-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-email">Email</Label>
            <Input
              id="profile-email"
              type="email"
              value={draft.email}
              onChange={setField('email')}
              data-testid="profile-input-email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-credentials">Credentials</Label>
            <Input
              id="profile-credentials"
              value={draft.credentials}
              onChange={setField('credentials')}
              placeholder="e.g. RSS, BHT, LCSW"
              data-testid="profile-input-credentials"
            />
            <p className="text-xs text-muted-foreground">
              Used on signed shift notes and reports.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <div
              className="flex items-center gap-2 px-3 h-9 rounded-md border bg-muted/40"
              data-testid="profile-role-display"
            >
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              <Badge
                variant={user?.role === 'admin' ? 'default' : 'secondary'}
                className="text-[10px]"
              >
                {(user?.role || 'staff').toUpperCase()}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Contact an admin to change your role.
              </span>
            </div>
          </div>

          {feedback && (
            <p
              className={
                feedback.type === 'success'
                  ? 'text-sm text-green-600 dark:text-green-400'
                  : 'text-sm text-destructive'
              }
              data-testid="profile-feedback"
            >
              {feedback.message}
            </p>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="profile-save-button">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 shadow-sm max-w-xl" data-testid="change-password-card">
        <h2 className="text-base font-semibold mb-4">Change Password</h2>
        <ChangePasswordForm token={user?.token} />
      </Card>
    </motion.div>
  );
};

const ChangePasswordForm = ({ token }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type, text }

  const submit = async () => {
    if (next.length < 8) { setMsg({ type: 'error', text: 'New password must be at least 8 characters.' }); return; }
    if (next !== confirm) { setMsg({ type: 'error', text: 'Passwords do not match.' }); return; }
    setBusy(true);
    setMsg(null);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCurrent(''); setNext(''); setConfirm('');
      setMsg({ type: 'success', text: 'Password updated.' });
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Could not update password.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="pw-current">Current Password</Label>
        <Input id="pw-current" type="password" value={current} onChange={(e) => { setCurrent(e.target.value); setMsg(null); }} autoComplete="current-password" data-testid="change-pw-current" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="pw-new">New Password</Label>
        <Input id="pw-new" type="password" value={next} onChange={(e) => { setNext(e.target.value); setMsg(null); }} autoComplete="new-password" placeholder="At least 8 characters" data-testid="change-pw-new" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="pw-confirm">Confirm New Password</Label>
        <Input id="pw-confirm" type="password" value={confirm} onChange={(e) => { setConfirm(e.target.value); setMsg(null); }} autoComplete="new-password" data-testid="change-pw-confirm" />
      </div>
      {msg && (
        <p className={msg.type === 'success' ? 'text-sm text-green-600 dark:text-green-400' : 'text-sm text-destructive'} data-testid="change-pw-feedback">
          {msg.text}
        </p>
      )}
      <div className="flex justify-end">
        <Button onClick={submit} disabled={busy || !current || !next || !confirm} data-testid="change-pw-submit">
          {busy ? 'Updating…' : 'Update Password'}
        </Button>
      </div>
    </div>
  );
};

export default Profile;
